import {
  Chart as ChartJS,
  ArcElement,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  TimeScale,
  Filler,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  ArcElement,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  TimeScale,
  Filler,
  ChartDataLabels,
);

const VerticalBarChart = ({
  dataChart,
  labelsChart,
  unitValueChart,
  colorChart,
  thresholdCm,
}) => {
  const thresholdMeters =
    typeof thresholdCm === "number" ? thresholdCm / 100 : null;

  const parseRangeLabel = (label) => {
    if (!label) return null;
    const normalized = String(label).replace("m", "");
    const [startStr, endStr] = normalized.split("-");
    const start = Number(startStr);
    const end = Number(endStr);
    if (isNaN(start) || isNaN(end)) return null;
    return { start, end };
  };

  const barColors = labelsChart.map((label) => {
    const range = parseRangeLabel(label);
    if (!range || thresholdMeters === null) {
      return colorChart ? colorChart : "#4BC0C0";
    }
    const isBelowThreshold = range.end <= thresholdMeters;
    return isBelowThreshold ? "#D1D5DB" : colorChart ? colorChart : "#4BC0C0";
  });

  const thresholdPlugin =
    thresholdMeters === null
      ? undefined
      : {
          id: "size-threshold-marker",
          afterDraw: (chart) => {
            const { ctx, chartArea, scales } = chart;
            const xScale = scales.x;
            if (!xScale) return;

            // Determine boundary index: first bucket whose start >= threshold
            const boundaryIndex = labelsChart.findIndex((label) => {
              const range = parseRangeLabel(label);
              if (!range) return false;
              return range.start >= thresholdMeters;
            });

            // If not found, do not draw (threshold beyond chart)
            if (boundaryIndex <= 0) return;

            const leftTick = xScale.getPixelForTick(boundaryIndex - 1);
            const rightTick = xScale.getPixelForTick(boundaryIndex);
            const x = (leftTick + rightTick) / 2;

            const topLabelOffsetPx = 20;

            ctx.save();
            ctx.strokeStyle = "#9CA3AF";
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 6]);
            ctx.beginPath();
            ctx.moveTo(x, chartArea.top);
            ctx.lineTo(x, chartArea.bottom);
            ctx.stroke();
            ctx.setLineDash([]);

            // Threshold label
            ctx.fillStyle = "#6B7280";
            ctx.font = "600 12px sans-serif";
            const thresholdText = `${thresholdCm}cm`;
            const textWidth = ctx.measureText(thresholdText).width;
            ctx.fillText(
              thresholdText,
              x - textWidth / 2,
              chartArea.top - topLabelOffsetPx,
            );

            ctx.restore();
          },
        };

  return (
    <Bar
      plugins={[thresholdPlugin].filter(Boolean)}
      data={{
        labels: labelsChart,
        datasets: [
          {
            label: "Count",
            data: dataChart,
            backgroundColor: barColors,
            borderWidth: 0,
          },
        ],
      }}
      options={{
        indexAxis: "x", // Vertical bars (default)
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 50,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                return `${context.parsed.y}${unitValueChart || ""}`;
              },
            },
          },
          datalabels: {
            anchor: "end",
            align: "top",
            formatter: (value) => {
              return `${value}${unitValueChart || ""}`;
            },
            color: "#111827",
            font: {
              weight: "bold",
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return `${value}${unitValueChart || ""}`;
              },
            },
          },
        },
      }}
    />
  );
};

export default VerticalBarChart;
